import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdvisorUsageView, ClientResponse } from '@clarity/contract';
import { useClient, useAdminAdvisorUsage, useSetAdvisorCap } from '../../queries/index.js';
import { QueryState } from '../QueryState.js';
import { money, num, errorStatus } from '../format.js';
import { IconSettings } from '../../components/icons.js';

/**
 * Admin-only AI-usage page for ONE client workspace (clarity-advisor-llm Req 4).
 *
 * A per-client admin surface mounted at `/clients/:clientId/advisor-usage`,
 * reached from the Clients list — the sibling of the subscription page and built
 * to the same frame (`.page-head`, `.card`, `QueryState`, the subscription stats
 * grid for the figures, the override set/clear form). It shows the workspace's
 * month-to-date AI spend vs its EFFECTIVE monthly cap (the per-workspace override
 * when set, else the global default), and lets the admin RAISE the limit by
 * agreement — or clear the override back to the default. The cap is per
 * WORKSPACE, so unlimited users of the client share one budget.
 *
 * The server enforces admin-only on both the read and the write (a client-role
 * caller gets 403); the route guard is the UI convenience. All amounts are USD
 * (the AI cost model), unlike the JOD subscription page.
 */
export function AdvisorUsageAdmin() {
  const { clientId } = useParams();
  const client = useClient(clientId ?? '', Boolean(clientId));
  const clientBody: ClientResponse | null = client.data?.status === 200 ? client.data.body : null;

  return (
    <section className="billing">
      <div className="page-head billing-head">
        <div>
          <Link to="/clients" className="billing-back">
            ← Clients
          </Link>
          <h2>{clientBody ? `${clientBody.name} — AI usage` : 'AI usage'}</h2>
          <p className="sub">
            This workspace&apos;s AI assistance spend this month and its monthly budget. Raise the
            cap by agreement when a workspace hits the limit. All amounts are in USD.
          </p>
        </div>
      </div>

      <QueryState isLoading={client.isLoading} isError={client.isError} label="client">
        {clientId ? (
          <AdvisorUsageCard key={clientId} clientId={clientId} />
        ) : (
          <div className="card empty">
            <IconSettings className="empty__glyph" />
            <p>Client not found.</p>
          </div>
        )}
      </QueryState>
    </section>
  );
}

/** Format a `YYYY-MM` month as a short "November 2026"-style label. */
function monthLabel(month: string): string {
  const d = new Date(`${month}-01T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The usage figures + cap set/clear controls for one workspace. Keyed by client
 * id in the parent so switching clients remounts with fresh local state. Mirrors
 * `SubscriptionCard`: the cap input seeds from the loaded override and is
 * RESEEDED via `useEffect`, so an errored save keeps the entered value while a
 * successful save (which invalidates → refetches) flips the field to the
 * server's returned override.
 */
function AdvisorUsageCard({ clientId }: { clientId: string }) {
  const usage = useAdminAdvisorUsage(clientId);
  const view: AdvisorUsageView | null = usage.data?.status === 200 ? usage.data.body : null;

  return (
    <QueryState isLoading={usage.isLoading} isError={usage.isError} label="AI usage">
      {view ? <UsagePanel clientId={clientId} view={view} /> : null}
    </QueryState>
  );
}

function UsagePanel({ clientId, view }: { clientId: string; view: AdvisorUsageView }) {
  const setCap = useSetAdvisorCap(clientId);

  // The cap input seeds from the stored override (blank when using the default).
  const [cap, setCapInput] = useState(view.overrideUsd === null ? '' : String(view.overrideUsd));

  // Reseed from the loaded view whenever the override changes (a successful write
  // refetches; an errored write does not, so the field keeps its entered value).
  useEffect(() => {
    setCapInput(view.overrideUsd === null ? '' : String(view.overrideUsd));
  }, [view.overrideUsd]);

  const capNum = cap.trim() === '' ? null : Number(cap);
  const capInvalid =
    capNum !== null && (!Number.isFinite(capNum) || capNum < 0 || capNum > 999999.99);
  const over = view.remainingUsd <= 0 && view.capUsd > 0;

  function onSetCap(e: FormEvent) {
    e.preventDefault();
    if (cap.trim() === '' || capInvalid) return;
    setCap.mutate({ params: { clientId }, body: { capUsd: Number(cap) } });
  }

  function onClearCap() {
    setCap.mutate({ params: { clientId }, body: { capUsd: null } });
  }

  const saved = setCap.data?.status === 200 && !setCap.isPending;
  const badRequest = setCap.isError && errorStatus(setCap.error) === 400;

  return (
    <div className="card subscription-card">
      <h3>AI budget — {monthLabel(view.month)}</h3>

      {/* The month-to-date figures (Req 4.1): spent / cap / remaining / calls. */}
      <dl className="subscription-stats">
        <div className="subscription-stat">
          <dt>Spent this month</dt>
          <dd className="num">{money(view.spentUsd, 'USD')}</dd>
        </div>
        <div className="subscription-stat">
          <dt>Monthly cap</dt>
          <dd className="num">{money(view.capUsd, 'USD')}</dd>
        </div>
        <div className={`subscription-stat${over ? '' : ' subscription-stat--total'}`}>
          <dt>Remaining</dt>
          <dd className="num">{money(view.remainingUsd, 'USD')}</dd>
        </div>
        <div className="subscription-stat">
          <dt>AI answers</dt>
          <dd className="num">{num(view.calls)}</dd>
        </div>
      </dl>

      {over && (
        <p role="status" className="muted">
          This workspace is over its cap — free-form AI answers are paused until next month or a
          higher cap. The quick answers (campaign status, balance, review, AM brief) still work.
        </p>
      )}

      {/* ── Cap override (Req 4.2): set to raise by agreement, clear to default ── */}
      <div className="subscription-edit">
        <form className="subscription-field" onSubmit={onSetCap} aria-label="Set AI cap override">
          <label htmlFor="advisor-cap">
            Monthly cap override (USD)
            <input
              id="advisor-cap"
              type="number"
              min={0}
              max={999999.99}
              step="0.01"
              value={cap}
              onChange={(e) => setCapInput(e.target.value)}
              placeholder="e.g. 20.00"
              aria-invalid={capInvalid || undefined}
            />
          </label>
          <div className="subscription-field__actions">
            <button type="submit" disabled={setCap.isPending || cap.trim() === '' || capInvalid}>
              {setCap.isPending ? 'Saving…' : 'Set cap'}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={onClearCap}
              disabled={setCap.isPending || view.overrideUsd === null}
            >
              {setCap.isPending ? 'Clearing…' : 'Clear override'}
            </button>
          </div>
          <p className="muted">
            {view.overrideUsd === null
              ? 'No override — this workspace uses the global default cap. Set one to raise its limit.'
              : `Override active: ${money(view.overrideUsd, 'USD')} per month. Clear it to revert to the global default.`}
          </p>
          {capInvalid && (
            <p role="alert" className="error">
              Enter a cap between 0 and 999,999.99, or leave it blank to use the default.
            </p>
          )}
          {setCap.isError && (
            <p role="alert" className="error">
              {badRequest
                ? 'That cap was rejected. Your entry is unchanged — try a value between 0 and 999,999.99.'
                : 'Could not save the cap. Your entry is unchanged — try again.'}
            </p>
          )}
          {saved && !setCap.isError && (
            <p role="status" className="muted">
              Saved.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
